import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Grid,
  Chip,
  Divider,
  useTheme,
  Tab,
  Tabs,
  IconButton,
  Card,
  CardContent,
} from "@mui/material";
import { MatchData, PredictionData } from "@/interfaces/liveGamesType";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  Tooltip,
  Legend,
  LabelList,
} from "recharts";
import { FiInfo, FiRefreshCw, FiTrendingUp } from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";
import { fetchGetPredictions } from "@/hooks/fetchHooks/getPredictionsFetch";
import NeonTabs, { TabPanel as NeonTabPanel, a11yProps } from '@/components/NeonTabs';
import { keyframes } from "@emotion/react";
import { darken, lighten, alpha } from '@mui/material/styles';
import { styled } from '@mui/material/styles';
import NeonCard from '@/components/NeonCard';
import { fakePredictionsData } from "../MatchDetails/fakeData";


// Keyframes para animações
const shimmerEffect = keyframes`
  0% {
    left: -100%;
  }
  100% {
    left: 100%;
  }
`;

const pulseAnimation = keyframes`
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.01);
  }
`;

const gridMoveAnimation = keyframes`
  0% {
    background-position: 0 0;
  }
  100% {
    background-position: 40px 40px;
  }
`;

const glowAnimation = keyframes`
  0%, 100% {
    box-shadow: 0 0 20px rgba(42, 244, 151, 0.4);
  }
  50% {
    box-shadow: 0 0 30px rgba(42, 244, 151, 0.7);
  }
`;

interface MatchPredictionsProps {
  match: MatchData;
  isLoading?: boolean;
}

// Estilos customizados para o PieChart
const StyledPieChart = styled(PieChart)({
  '& .legend-item-0': {
    display: 'none !important'
  }
});

// Componente principal
const MatchPredictions: React.FC<MatchPredictionsProps> = ({
  match
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const { isAuthenticated, decodedToken } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  const {
    data: predictions,
    isLoading: predictionsLoading,
    refetch,
  } = isAuthenticated && decodedToken.isActive ? fetchGetPredictions(match?.match_id, {
    enabled: !!match?.match_id,
  }) : {
    data: fakePredictionsData,
    isLoading: false,
    refetch: () => Promise.resolve(),
  };
console.log(decodedToken.isActive, isAuthenticated,"cummkkk")
  // Dados para o gráfico de pizza de resultado
  const resultData = useMemo(() => {
    if (!predictions) return [];

    // Converter valores para números e garantir que são válidos
    const homeWin = parseFloat(predictions.prob_HW) || 0;
    const draw = parseFloat(predictions.prob_D) || 0;
    const awayWin = parseFloat(predictions.prob_AW) || 0;

    // Verificar se existe pelo menos um valor maior que zero
    const hasValidData = homeWin > 0 || draw > 0 || awayWin > 0;

    // Se não houver dados válidos, retornar array vazio
    if (!hasValidData) {
      console.warn("Dados de predição inválidos ou inexistentes:", predictions);
      return [];
    }

    return [
      {
        name: "Vitória Casa",
        value: homeWin,
        color: theme.palette.homeColor.main,
      },
      { name: "Empate", value: draw, color: theme.palette.grey[500] },
      { name: "Vitória Fora", value: awayWin, color: theme.palette.awayColor.main },
    ];
  }, [predictions, theme]);

  // Dados para o gráfico de barras de tendências
  const trendsData = useMemo(() => {
    if (!predictions) return [];

    return [
      {
        name: "Over 1.5",
        value: parseFloat(predictions.prob_O_1),
        color:
          parseFloat(predictions.prob_O_1) > 50
            ? theme.palette.success.main
            : theme.palette.error.main,
      },
      {
        name: "Under 1.5",
        value: parseFloat(predictions.prob_U_1),
        color:
          parseFloat(predictions.prob_U_1) > 50
            ? theme.palette.success.main
            : theme.palette.error.main,
      },
      {
        name: "Over 2.5",
        value: parseFloat(predictions.prob_O),
        color:
          parseFloat(predictions.prob_O) > 50
            ? theme.palette.success.main
            : theme.palette.error.main,
      },
      {
        name: "Under 2.5",
        value: parseFloat(predictions.prob_U),
        color:
          parseFloat(predictions.prob_U) > 50
            ? theme.palette.success.main
            : theme.palette.error.main,
      },
      {
        name: "Over 3.5",
        value: parseFloat(predictions.prob_O_3),
        color:
          parseFloat(predictions.prob_O_3) > 50
            ? theme.palette.success.main
            : theme.palette.error.main,
      },
      {
        name: "Under 3.5",
        value: parseFloat(predictions.prob_U_3),
        color:
          parseFloat(predictions.prob_U_3) > 50
            ? theme.palette.success.main
            : theme.palette.error.main,
      },
      {
        name: "Ambas Marcam",
        value: parseFloat(predictions.prob_bts),
        color:
          parseFloat(predictions.prob_bts) > 50
            ? theme.palette.success.main
            : theme.palette.error.main,
      }
    ];
  }, [predictions, theme]);

  console.log({ predictions, trendsData, resultData });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    refetch().finally(() => {
      setTimeout(() => {
        setIsRefreshing(false);
      }, 600); // Mantém a animação por um tempo após o término do refetch
    });
  };

  // Função para calcular o placar mais provável com base nos dados estatísticos
  const calculatePossibleScores = (predictions: PredictionData | undefined) => {
    if (!predictions) return { mainScore: { home: "?", away: "?", probability: 0 }, alternativeScores: [] };
    
    // Converter valores para números e garantir que são válidos
    const homeWinProb = parseFloat(predictions.prob_HW) / 100 || 0;
    const drawProb = parseFloat(predictions.prob_D) / 100 || 0;
    const awayWinProb = parseFloat(predictions.prob_AW) / 100 || 0;
    
    // Parâmetros para a distribuição de gols baseados nos novos dados
    const over2_5Prob = parseFloat(predictions.prob_O) / 100 || 0.5;
    const under2_5Prob = parseFloat(predictions.prob_U) / 100 || 0.5;
    const bothTeamScore = parseFloat(predictions.prob_bts) / 100 || 0.5;
    const onlyOneTeamScore = parseFloat(predictions.prob_ots) / 100 || 0.5;
    
    // Estimar média de gols baseado nas probabilidades de over/under
    let expectedTotalGoals;
    if (over2_5Prob > 0.6) {
      expectedTotalGoals = 3.2;
    } else if (over2_5Prob > 0.4) {
      expectedTotalGoals = 2.5;
    } else {
      expectedTotalGoals = 1.8;
    }
    
    // Criar array de possíveis placares com suas probabilidades
    const possibleScores = [];
    
    // Função para calcular probabilidade melhorada para um placar específico
    const calculateScoreProbability = (home: number, away: number) => {
      // Inicializar com um valor base
      let totalWeight = 0;
      let weightedProbability = 0;
      
      // Atribuir peso para o resultado (vitória casa, empate, vitória fora)
      const resultWeight = 3.0; // Peso maior para o resultado principal
      if (home > away) {
        weightedProbability += homeWinProb * resultWeight;
      } else if (home === away) {
        weightedProbability += drawProb * resultWeight;
      } else {
        weightedProbability += awayWinProb * resultWeight;
      }
      totalWeight += resultWeight;
      
      // Atribuir peso para over/under 2.5
      const overUnderWeight = 2.0;
      const totalGoals = home + away;
      if (totalGoals > 2.5) {
        weightedProbability += over2_5Prob * overUnderWeight;
      } else {
        weightedProbability += under2_5Prob * overUnderWeight;
      }
      totalWeight += overUnderWeight;
      
      // Atribuir peso para over/under 3.5
      const overUnder35Weight = 1.5;
      if (totalGoals > 3.5) {
        const over35Prob = parseFloat(predictions.prob_O_3) / 100 || 0.3;
        weightedProbability += over35Prob * overUnder35Weight;
      } else {
        const under35Prob = parseFloat(predictions.prob_U_3) / 100 || 0.7;
        weightedProbability += under35Prob * overUnder35Weight;
      }
      totalWeight += overUnder35Weight;
      
      // Atribuir peso para over/under 1.5
      const overUnder15Weight = 1.5;
      if (totalGoals > 1.5) {
        const over15Prob = parseFloat(predictions.prob_O_1) / 100 || 0.7;
        weightedProbability += over15Prob * overUnder15Weight;
      } else {
        const under15Prob = parseFloat(predictions.prob_U_1) / 100 || 0.3;
        weightedProbability += under15Prob * overUnder15Weight;
      }
      totalWeight += overUnder15Weight;
      
      // Atribuir peso para ambas equipes marcarem
      const btsWeight = 1.5;
      if (home > 0 && away > 0) {
        weightedProbability += bothTeamScore * btsWeight;
      } else {
        weightedProbability += onlyOneTeamScore * btsWeight;
      }
      totalWeight += btsWeight;
      
      // Usar handicap asiático para ajustar a probabilidade
      const handicapWeight = 1.0;
      const goalDiff = Math.abs(home - away);
      if (goalDiff >= 2 && home > away) {
        // Limitar valores extremos de handicap
        const handicapFactor = Math.min(0.85, parseFloat(predictions.prob_ah_h_15) / 100 || 0.5);
        weightedProbability += handicapFactor * handicapWeight;
      } else if (goalDiff >= 2 && away > home) {
        // Limitar valores extremos de handicap
        const handicapFactor = Math.min(0.85, parseFloat(predictions.prob_ah_a_15) / 100 || 0.5);
        weightedProbability += handicapFactor * handicapWeight;
      } else {
        // Se não há handicap grande, adiciona um valor neutro
        weightedProbability += 0.5 * handicapWeight;
      }
      totalWeight += handicapWeight;
      
      // Calcular média ponderada das probabilidades
      let finalProbability = weightedProbability / totalWeight;
      
      // Adicionar ajustes para placares comuns
      if ((home === 1 && away === 0) || (home === 0 && away === 1) || 
          (home === 1 && away === 1) || (home === 2 && away === 1) || 
          (home === 1 && away === 2) || (home === 2 && away === 0) ||
          (home === 0 && away === 2)) {
        finalProbability *= 1.2; // Reduzido de 1.3 para 1.2
      }
      
      // Para empates sem gols
      if (home === 0 && away === 0) {
        finalProbability *= parseFloat(predictions.prob_U_1) > 60 ? 1.3 : 0.7; // Reduzido de 1.5 para 1.3
      }
      
      // Para placares muito altos, reduzir a probabilidade
      if (home + away > 5) {
        finalProbability *= 0.6; // Reduzido de 0.7 para 0.6
      }
      
      // Aplicar uma limitação para evitar probabilidades extremamente altas
      finalProbability = Math.min(finalProbability, 0.7);
      
      // Converter para porcentagem (0-100) e arredondar
      return Math.round(finalProbability * 100);
    };
    
    // Gerar placares possíveis (limitado a 5 gols por equipe)
    const maxGoals = parseFloat(predictions.prob_O) > 50 ? 5 : 4;
    
    for (let home = 0; home <= maxGoals; home++) {
      for (let away = 0; away <= maxGoals; away++) {
        const probability = calculateScoreProbability(home, away);
        possibleScores.push({
          home: home.toString(),
          away: away.toString(),
          probability
        });
      }
    }
    
    // Ordenar por probabilidade (decrescente) primeiro
    possibleScores.sort((a, b) => b.probability - a.probability);
    
    // Pegar os 4 placares mais prováveis
    const topScores = possibleScores.slice(0, 4);

    // Retornar o placar mais provável e os 3 alternativos
    return {
      mainScore: topScores[0],
      alternativeScores: topScores.slice(1, 4)
    };
  };

  // Dentro do componente principal
  const scoresPrediction = useMemo(() => {
    return calculatePossibleScores(predictions);
  }, [predictions]);

  if (predictionsLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: 300,
        }}
      >
        <CircularProgress size={30} />
      </Box>
    );
  }

  // Renderizar o gráfico de pizza de resultado
  const renderResultPieChart = () => (
    <Box sx={{ height: 380, mt: 2 }}>
      {resultData.length > 0 ? (
        <ResponsiveContainer width="100%" height="100%">
          <StyledPieChart>
            <defs>
              {/* Gradientes para cada fatia do gráfico */}
              <radialGradient id="homeWinGradient" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
                <stop offset="0%" stopColor={lighten(theme.palette.homeColor.main, 0.3)} stopOpacity={1} />
                <stop offset="100%" stopColor={darken(theme.palette.homeColor.main, 0.2)} stopOpacity={0.8} />
              </radialGradient>
              
              <radialGradient id="drawGradient" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
                <stop offset="0%" stopColor={lighten(theme.palette.grey[500], 0.3)} stopOpacity={1} />
                <stop offset="100%" stopColor={darken(theme.palette.grey[500], 0.3)} stopOpacity={0.8} />
              </radialGradient>
              
              <radialGradient id="awayWinGradient" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
                <stop offset="0%" stopColor={lighten(theme.palette.awayColor.main, 0.3)} stopOpacity={1} />
                <stop offset="100%" stopColor={darken(theme.palette.awayColor.main, 0.2)} stopOpacity={0.8} />
              </radialGradient>
              
              {/* Filtro para efeito neon */}
              <filter id="pie-neon-glow" height="300%" width="300%" x="-100%" y="-100%">
                <feGaussianBlur stdDeviation="5" result="blur" />
                <feColorMatrix
                  in="blur"
                  type="matrix"
                  values="0 0 0 0 0.2
                          0 0 0 0 1
                          0 0 0 0 0.5
                          0 0 0 0.6 0"
                />
                <feBlend in="SourceGraphic" in2="blur" mode="normal" />
              </filter>
            </defs>
            
            {/* Círculo de fundo com efeito pulsante como parte do componente Pie */}
            <Pie
              data={[{ name: "", value: 1 }]}
              cx="50%"
              cy="50%"
              outerRadius={110}
              innerRadius={50}
              dataKey="value"
              stroke="rgba(42, 244, 151, 0.2)"
              strokeWidth={2}
              fill="none"
              isAnimationActive={false}
              legendType="none"
            />
            
            <Pie
              data={resultData}
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={100}
              paddingAngle={3}
              dataKey="value"
              label={({ value, name, cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
                const RADIAN = Math.PI / 180;
                const radius = 25 + innerRadius + (outerRadius - innerRadius);
                const x = cx + radius * Math.cos(-midAngle * RADIAN);
                const y = cy + radius * Math.sin(-midAngle * RADIAN);
                
                return (
                  <text 
                    x={x} 
                    y={y} 
                    fill="#ffffff" 
                    textAnchor={x > cx ? 'start' : 'end'} 
                    dominantBaseline="central"
                    style={{
                      fontSize: 16,
                      fontWeight: 'bold',
                      textShadow: '0 0 8px rgba(42, 244, 151, 0.8)'
                    }}
                  >
                    {`${value}%`}
                  </text>
                );
              }}
              labelLine={false}
              animationDuration={1500}
              animationBegin={0}
              strokeWidth={2}
            >
              {resultData.map((entry, index) => {
                // Determinar qual gradiente usar com base no nome da entrada
                let fillId;
                if (entry.name === "Vitória Casa") fillId = "url(#homeWinGradient)";
                else if (entry.name === "Empate") fillId = "url(#drawGradient)";
                else fillId = "url(#awayWinGradient)";
                
                return (
                  <Cell
                    key={`cell-${index}`}
                    fill={fillId}
                    stroke={isDarkMode ? "#333" : "#fff"}
                    strokeWidth={2}
                    style={{
                      filter: entry.value > 50 ? 'url(#pie-neon-glow)' : 'none',
                      cursor: 'pointer'
                    }}
                  />
                );
              })}
            </Pie>
            <Tooltip
              formatter={(value, name) => [`${value}%`, name]}
              contentStyle={{
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                border: '1px solid #2af497',
                borderRadius: '8px',
                color: '#fff',
                boxShadow: '0 0 15px rgba(42, 244, 151, 0.5)'
              }}
              itemStyle={{
                color: '#fff'
              }}
            />
            <Legend
              verticalAlign="bottom"
              iconType="circle"
              formatter={(value) => (
                <span style={{ color: '#fff', textShadow: '0 0 5px rgba(42, 244, 151, 0.5)' }}>
                  {value}
                </span>
              )}
            />
          </StyledPieChart>
        </ResponsiveContainer>
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          <Typography color="text.secondary">
            Dados insuficientes para exibir o gráfico
          </Typography>
        </Box>
      )}
    </Box>
  );

  // Renderizar o gráfico de barras de tendências
  const renderTrendBars = () => (
    <Box sx={{ height: 300, mt: 2 }}>
      {trendsData.length > 0 ? (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={trendsData}
            margin={{ top: 20, right: 30, left: 20, bottom: 50 }}
          >
            <defs>
              {/* Gradientes personalizados para cada tipo de dado */}
              {trendsData.map((entry, index) => (
                <linearGradient
                  key={`gradient-${index}`}
                  id={`colorBar${index}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="0%"
                    stopColor={entry.value > 50 
                      ? lighten(theme.palette.success.main, 0.1) 
                      : lighten(theme.palette.error.main, 0.1)}
                    stopOpacity={0.95}
                  />
                  <stop
                    offset="100%"
                    stopColor={entry.value > 50 
                      ? darken(theme.palette.success.main, 0.3) 
                      : darken(theme.palette.error.main, 0.3)}
                    stopOpacity={0.8}
                  />
                </linearGradient>
              ))}
              
              {/* Filtro para efeito neon */}
              <filter id="neon-glow" height="300%" width="300%" x="-100%" y="-100%">
                <feGaussianBlur stdDeviation="5" result="blur" />
                <feColorMatrix
                  in="blur"
                  type="matrix"
                  values="0 0 0 0 0
                          0 1 0 0 0
                          0 0 1 0 0
                          0 0 0 1 0"
                />
                <feBlend in="SourceGraphic" in2="blur" mode="normal" />
              </filter>
            </defs>
            
            <XAxis
              dataKey="name"
              angle={-45}
              textAnchor="end"
              height={80}
              interval={0}
              tick={{ fontSize: 15 }}
              tickMargin={5}
              stroke="#73f0b7"
            />
            <Tooltip
              formatter={(value, name, entry) => {
                return [`${value}%`, entry.payload.name];
              }}
              contentStyle={{
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                border: '1px solid #2af497',
                borderRadius: '8px',
                color: '#fff',
                boxShadow: '0 0 15px rgba(42, 244, 151, 0.5)'
              }}
              itemStyle={{
                color: '#fff'
              }}
              cursor={false}
              labelFormatter={() => ""}
            />
            <Bar 
              dataKey="value" 
              radius={[10, 10, 0, 0]}
              minPointSize={2}
              barSize={40}
              animationDuration={1500}
              animationBegin={0}
            >
              {trendsData.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={`url(#colorBar${index})`}
                  style={{
                    filter: entry.value > 65 ? 'url(#neon-glow)' : 'none',
                    cursor: 'pointer'
                  }}
                />
              ))}
              <LabelList
                dataKey="value"
                position="top"
                formatter={(value: number) => `${value}%`}
                style={{
                  fontSize: "14px",
                  fontWeight: "bold",
                  fill: "#fff",
                  textShadow: "0 0 5px rgba(42, 244, 151, 0.8)"
                }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          <Typography color="text.secondary">
            Dados insuficientes para exibir o gráfico
          </Typography>
        </Box>
      )}
    </Box>
  );

  // Renderizar o placar mais provável
  const renderLikelyScore = () => (
    <Box
      sx={{
        mt: 2,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
      }}
    >
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5 }}
        style={{ width: "100%", maxWidth: 360 }}
      >
        <Card
          elevation={8}
          sx={{
            width: "100%",
            borderRadius: 4,
            overflow: "hidden",
            background: `linear-gradient(135deg, ${darken(theme.palette.homeColor.main, 0.4)} 0%, ${theme.palette.homeColor.main} 100%)`,
            mb: 2
          }}
        >
          <Box sx={{ p: 2, textAlign: "center", color: "white" }}>
            <Typography variant="h6" fontWeight="bold">
              Placar Mais Provável
            </Typography>
            {scoresPrediction.mainScore.probability > 0 && (
              <Chip 
                label={`${scoresPrediction.mainScore.probability}% de probabilidade`} 
                size="small" 
                sx={{ 
                  mt: 0.5, 
                  bgcolor: 'rgba(255,255,255,0.2)', 
                  color: 'white',
                  fontWeight: 'bold'
                }} 
              />
            )}
          </Box>
          <CardContent>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Box sx={{ textAlign: "center", width: "40%" }}>
                <Typography variant="body2" color="white" noWrap>
                  {match.match_hometeam_name}
                </Typography>
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      width: 60,
                      height: 60,
                      borderRadius: "50%",
                      bgcolor: "rgba(255, 255, 255, 0.9)",
                      color: theme.palette.homeColor.main,
                      fontWeight: "bold",
                      fontSize: "2rem",
                      m: "auto",
                      mt: 1,
                      boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                    }}
                  >
                    {scoresPrediction.mainScore.home}
                  </Box>
                </motion.div>
              </Box>

              <Typography
                variant="h5"
                color="white"
                sx={{ fontWeight: "bold" }}
              >
                vs
              </Typography>

              <Box sx={{ textAlign: "center", width: "40%" }}>
                <Typography variant="body2" color="white" noWrap>
                  {match.match_awayteam_name}
                </Typography>
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.4, duration: 0.5 }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      width: 60,
                      height: 60,
                      borderRadius: "50%",
                      bgcolor: "rgba(255, 255, 255, 0.9)",
                      color: theme.palette.awayColor.main,
                      fontWeight: "bold",
                      fontSize: "2rem",
                      m: "auto",
                      mt: 1,
                      boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                    }}
                  >
                    {scoresPrediction.mainScore.away}
                  </Box>
                </motion.div>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Placares Alternativos */}
      {scoresPrediction.alternativeScores.length > 0 && <Typography variant="subtitle1" color="textSecondary" sx={{ ml: 1, mb: 1, fontWeight: "bold" }}>
          Placares Alternativos:
        </Typography>}
        <Grid container spacing={2}>
          {scoresPrediction.alternativeScores.map((score, index) => (
            <Grid item xs={12} key={`alt-score-${index}`}>
              <motion.div
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.2 + (index * 0.1), duration: 0.4 }}
              >
                <Card
                  variant="outlined"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    p: 1,
                    borderRadius: 2,
                    bgcolor: theme.palette.background.paper,
                    boxShadow: 1
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center",justifyContent: "center",gap: 1,flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: "bold", textAlign: "right" }}>
                      {match.match_hometeam_name.split(' ')[0]}
                    </Typography>
                    <Box
                      sx={{
                        px: 1.5,
                        py: 0.5,
                        bgcolor: theme.palette.homeColor.main,
                        color: "white",
                        borderRadius: 1,
                        fontWeight: "bold",
                        display: "inline-block",
                        minWidth: 28,
                        textAlign: "center"
                      }}
                    >
                      {score.home}
                    </Box>
                    <Typography variant="body2" sx={{ mx: 0.5 }}>-</Typography>
                    <Box
                      sx={{
                        px: 1.5,
                        py: 0.5,
                        bgcolor: theme.palette.awayColor.main,
                        color: "white",
                        borderRadius: 1,
                        fontWeight: "bold",
                        display: "inline-block",
                        minWidth: 28,
                        textAlign: "center"
                      }}
                    >
                      {score.away}
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: "bold"}}>
                      {match.match_awayteam_name.split(' ')[0]}
                    </Typography>
                  </Box>
                  <Chip 
                    size="small" 
                    label={`${score.probability}%`} 
                    sx={{ ml: 1, minWidth: 50 }} 
                    color={score.probability > 25 ? "success" : "default"}
                  />
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </motion.div>
    </Box>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <NeonCard
        neonGlowIntensity="medium"
        tooltipInfo="Predições geradas por algoritmos de machine learning treinados com milhares de jogos históricos. Analisa padrões estatísticos, desempenho das equipes e tendências recentes para oferecer probabilidades precisas. Use estas informações para tomar decisões mais fundamentadas."
        cardTitle={
          <>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              zIndex={1}
              width="100%"
            >
            Predições Avançadas{" "}
              <IconButton
                onClick={handleRefresh}
                size="small"
                title="Atualizar dados"
                disabled={isRefreshing}
                sx={{
                  color: "#2af497",
                  "&:hover": {
                    background: "rgba(42, 244, 151, 0.1)",
                  },
                }}
              >
                <motion.div
                  animate={isRefreshing ? { rotate: 360 } : { rotate: 0 }}
                  transition={{
                    duration: 0.6,
                    ease: "linear",
                    repeat: isRefreshing ? Infinity : 0,
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transformOrigin: "center center",
                  }}
                >
                  <FiRefreshCw />
                </motion.div>
              </IconButton>
            </Box>
          </>
        }
      >
        <NeonTabs
          value={tabValue}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{ mb: 2, position: "relative", zIndex: 1 }}
        >
          <Tab label="Resultado" {...a11yProps(0, "predictions")} />
          <Tab label="Tendências" {...a11yProps(1, "predictions")} />
          <Tab label="Placar Provável" {...a11yProps(2, "predictions")} />
        </NeonTabs>

        <Box
          sx={{
            position: "relative",
          }}
        >
          <NeonTabPanel value={tabValue} index={0} prefix="predictions">
            {renderResultPieChart()}
          </NeonTabPanel>
          <NeonTabPanel value={tabValue} index={1} prefix="predictions">
            {renderTrendBars()}
          </NeonTabPanel>
          <NeonTabPanel value={tabValue} index={2} prefix="predictions">
            {renderLikelyScore()}
          </NeonTabPanel>
        </Box>

        <Divider
          sx={{
            my: 2,
            borderColor: "rgba(42, 244, 151, 0.2)",
            position: "relative",
            zIndex: 1,
          }}
        />

        <Grid
          container
          spacing={2}
          alignItems="flex-end"
          sx={{ position: "relative", zIndex: 1 }}
        >
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: "center" }}>
              <Typography
                variant="body2"
                color="rgba(255, 255, 255, 0.8)"
                sx={{ mb: 1 }}
              >
                Vitória {match.match_hometeam_name}
              </Typography>
              <Chip
                label={`${
                  predictions?.prob_HW ? parseInt(predictions?.prob_HW) : "??"
                }%`}
                size="medium"
                sx={{
                  fontWeight: "bold",
                  fontSize: "1.1rem",
                  my: 1,
                  backgroundColor: theme.palette.homeColor.main,
                  color: "white",
                  boxShadow: `0 0 15px ${alpha(
                    theme.palette.homeColor.main,
                    0.6
                  )}`,
                  textShadow: "0 0 5px rgba(255, 255, 255, 0.5)",
                  border: `1px solid ${lighten(
                    theme.palette.homeColor.main,
                    0.3
                  )}`,
                  position: "relative",
                  overflow: "hidden",
                  transition: "all 0.3s ease",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: "-100%",
                    width: "100%",
                    height: "100%",
                    background:
                      "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)",
                    animation: `${shimmerEffect} 2s infinite`,
                  },
                  "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: `0 0 20px ${alpha(
                      theme.palette.homeColor.main,
                      0.8
                    )}`,
                    backgroundColor: lighten(theme.palette.homeColor.main, 0.1),
                  },
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: "center" }}>
              <Typography
                variant="body2"
                color="rgba(255, 255, 255, 0.8)"
                sx={{ mb: 1 }}
              >
                Empate
              </Typography>
              <Chip
                label={`${
                  predictions?.prob_D ? parseInt(predictions?.prob_D) : "??"
                }%`}
                size="medium"
                sx={{
                  fontWeight: "bold",
                  fontSize: "1.1rem",
                  my: 1,
                  backgroundColor: alpha(theme.palette.grey[500], 0.3),
                  color: "white",
                  boxShadow: `0 0 10px ${alpha(theme.palette.grey[500], 0.5)}`,
                  textShadow: "0 0 5px rgba(255, 255, 255, 0.5)",
                  border: `1px solid ${lighten(theme.palette.grey[500], 0.2)}`,
                  position: "relative",
                  overflow: "hidden",
                  transition: "all 0.3s ease",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: "-100%",
                    width: "100%",
                    height: "100%",
                    background:
                      "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)",
                    animation: `${shimmerEffect} 2s infinite`,
                    animationDelay: "0.5s",
                  },
                  "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: `0 0 15px ${alpha(
                      theme.palette.grey[500],
                      0.7
                    )}`,
                    backgroundColor: alpha(theme.palette.grey[500], 0.4),
                  },
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: "center" }}>
              <Typography
                variant="body2"
                color="rgba(255, 255, 255, 0.8)"
                sx={{ mb: 1 }}
              >
                Vitória {match.match_awayteam_name}
              </Typography>
              <Chip
                label={`${
                  predictions?.prob_AW ? parseInt(predictions?.prob_AW) : "??"
                }%`}
                size="medium"
                sx={{
                  fontWeight: "bold",
                  fontSize: "1.1rem",
                  my: 1,
                  backgroundColor: theme.palette.awayColor.main,
                  color: "white",
                  boxShadow: `0 0 15px ${alpha(
                    theme.palette.awayColor.main,
                    0.6
                  )}`,
                  textShadow: "0 0 5px rgba(255, 255, 255, 0.5)",
                  border: `1px solid ${lighten(
                    theme.palette.awayColor.main,
                    0.3
                  )}`,
                  position: "relative",
                  overflow: "hidden",
                  transition: "all 0.3s ease",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: "-100%",
                    width: "100%",
                    height: "100%",
                    background:
                      "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)",
                    animation: `${shimmerEffect} 2s infinite`,
                    animationDelay: "1s",
                  },
                  "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: `0 0 20px ${alpha(
                      theme.palette.awayColor.main,
                      0.8
                    )}`,
                    backgroundColor: lighten(theme.palette.awayColor.main, 0.1),
                  },
                }}
              />
            </Box>
          </Grid>
        </Grid>

        {predictions ? (
          <Box mt={2} sx={{ position: "relative", zIndex: 1 }}>
            <Typography
              variant="body2"
              align="center"
              sx={{
                color: "rgba(42, 244, 151, 0.8)",
                textShadow: "0 0 5px rgba(42, 244, 151, 0.3)",
                fontSize: "0.85rem",
                fontStyle: "italic",
              }}
            >
              Predições baseadas em análise avançada de dados e machine learning
            </Typography>
          </Box>
        ) : (
          <Box mt={2} sx={{ position: "relative", zIndex: 1 }}>
            <Typography
              variant="body2"
              align="center"
              color="rgba(255, 255, 255, 0.6)"
            >
              Não há dados suficientes para gerar predições
            </Typography>
          </Box>
        )}
      </NeonCard>
    </motion.div>
  );
};

export default MatchPredictions;
